var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { useState, useCallback } from 'react';
import * as locator from '@arcgis/core/rest/locator.js';
import { arcGISGeocodeUrl, geographicPoint } from '../constants';
const attribute = (attributes, key) => {
    const value = attributes.get(key);
    return typeof value === 'string' && value.trim() !== '' ? value.trim() : null;
};
// Reverse-geocode a coordinate to a "City, ST" label for display.
export const locationToPlaceName = (point) => __awaiter(void 0, void 0, void 0, function* () {
    var _a, _b, _c, _d;
    try {
        const result = yield locator.locationToAddress(arcGISGeocodeUrl, {
            location: geographicPoint(point[0], point[1]),
        });
        const attributes = new Map(Object.entries((_a = result.attributes) !== null && _a !== void 0 ? _a : {}));
        const city = (_c = (_b = attribute(attributes, 'City')) !== null && _b !== void 0 ? _b : attribute(attributes, 'Neighborhood')) !== null && _c !== void 0 ? _c : attribute(attributes, 'Subregion');
        const region = (_d = attribute(attributes, 'RegionAbbr')) !== null && _d !== void 0 ? _d : attribute(attributes, 'Region');
        return city && region ? `${city}, ${region}` : city !== null && city !== void 0 ? city : region;
    }
    catch (err) {
        console.error('Reverse geocoding error:', err);
        return null;
    }
});
export function useGeocoding() {
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState(null);
    // `near` ranks addresses close to it first, for a caller that knows where the person is looking.
    // `categories` narrows what is suggested: street addresses unless the caller asks for, say, cities.
    const searchAddress = useCallback((searchText_1, near_1, ...args_1) => __awaiter(this, [searchText_1, near_1, ...args_1], void 0, function* (searchText, near, categories = ['Address']) {
        if (!searchText || searchText.length < 3) {
            return [];
        }
        setIsLoading(true);
        setError(null);
        try {
            const response = yield locator.suggestLocations(arcGISGeocodeUrl, Object.assign({ text: searchText, categories }, (near === undefined ? {} : { location: geographicPoint(near[0], near[1]) })), { query: { countryCode: 'USA' } });
            if (response && response.length > 0) {
                const addressPromises = response.slice(0, 5).map((suggestion) => __awaiter(this, void 0, void 0, function* () {
                    try {
                        const addressResult = yield locator.addressToLocations(arcGISGeocodeUrl, {
                            address: { SingleLine: suggestion.text },
                            maxLocations: 1,
                            magicKey: suggestion.magicKey || undefined,
                        });
                        if (addressResult && addressResult.length > 0) {
                            const result = addressResult[0];
                            if (!result.location) {
                                return null;
                            }
                            return {
                                address: result.address,
                                location: [result.location.x, result.location.y],
                                score: result.score,
                                attributes: result.attributes,
                            };
                        }
                        return null;
                    }
                    catch (err) {
                        console.error('Error geocoding suggestion:', err);
                        return null;
                    }
                }));
                const addresses = yield Promise.all(addressPromises);
                setIsLoading(false);
                return addresses.filter((addr) => addr !== null);
            }
            setIsLoading(false);
            return [];
        }
        catch (err) {
            console.error('Geocoding error:', err);
            setError(err instanceof Error ? err.message : 'Failed to geocode address');
            setIsLoading(false);
            return [];
        }
    }), []);
    return {
        searchAddress,
        isLoading,
        error,
    };
}
